import { FC, useEffect } from 'react';
import {
  Box,
  Button,
  Text,
  FormControl,
  FormErrorMessage,
  HStack,
  Heading,
  Input,
  VStack,
} from '@chakra-ui/react';
import {
  Control,
  FieldErrors,
  UseFormRegister,
  useFieldArray,
} from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { ProjectForm } from '../models';
import { getTomorrowsDate } from '../../../utils';

const { today, tomorrow } = getTomorrowsDate();

const newPhase = {
  phaseName: '',
  phaseEstimatedEndDate: today.toString(),
  phaseStartDate: tomorrow.toString(),
  phaseCode: '',
};

export const NewPhase: FC<{
  control: Control<ProjectForm>;
  register: UseFormRegister<ProjectForm>;
  errors: FieldErrors<ProjectForm>;
  handleAddPhase: (isAdded: boolean) => void;
  isLoading: boolean;
}> = ({ control, register, errors, handleAddPhase, isLoading }) => {
  const { phaseId } = useParams();

  const { fields, append } = useFieldArray({
    control,
    name: `phases`,
    rules: {
      minLength: 1,
    },
  });

  return (
    <Box>
      <HStack justify="space-between" mt="4" mb="12" align="center">
        <Heading color="custom_blue.1" fontSize="2xl">
          Phases Definition
        </Heading>

        <HStack mt={4} justify="end">
          <Button onClick={() => handleAddPhase(false)}>Cancel</Button>
          <Button
            isLoading={isLoading}
            type="button"
            onClick={() => handleAddPhase(true)}
            bg="custom_orange.2"
            _hover={{ bg: 'custom_orange.2' }}
            color="white"
          >
            Save
          </Button>
        </HStack>
      </HStack>

      <VStack as="form" align="stretch">
        <Box>
          {fields.map(({ id }, index) => (
            <VStack
              align="start"
              w="full"
              p="4"
              borderRadius="md"
              border="1px"
              borderColor="custom_gray.3"
              gap="4"
              key={id}
              mb="5"
              bg="white"
            >
              <HStack gap="4" align="flex-start" w="full">
                <VStack color="custom_blue.1" align="start" flexBasis="25%">
                  <Box fontSize="sm" fontWeight="semibold">
                    Phase Name
                  </Box>
                </VStack>
                <FormControl
                  isInvalid={errors.phases && !!errors.phases[index]?.phaseName}
                  flexBasis="50%"
                  mt="2"
                >
                  <Input
                    placeholder="Phase Name"
                    {...register(`phases.${index}.phaseName`, {
                      required: 'Phase Name is required',
                    })}
                  />
                  <FormErrorMessage>
                    {errors.phases &&
                      errors.phases[index]?.phaseName &&
                      errors.phases[index]?.phaseName?.message}
                  </FormErrorMessage>
                </FormControl>
              </HStack>

              <HStack gap="4" align="flex-start" w="full">
                <VStack color="custom_blue.1" align="start" flexBasis="25%">
                  <Box fontSize="sm" fontWeight="semibold">
                    Phase Code (Optional)
                  </Box>
                </VStack>
                <FormControl
                  isInvalid={errors.phases && !!errors.phases[index]?.phaseCode}
                  flexBasis="50%"
                  mt="2"
                >
                  <Input
                    placeholder="Phase Code"
                    {...register(`phases.${index}.phaseCode`)}
                  />
                  <FormErrorMessage>
                    {errors.phases &&
                      errors.phases[index]?.phaseCode &&
                      errors.phases[index]?.phaseCode?.message}
                  </FormErrorMessage>
                </FormControl>
              </HStack>

              <HStack gap="4" align="flex-start" w="full">
                <VStack color="custom_blue.1" align="start" flexBasis="25%">
                  <Box fontSize="sm" fontWeight="semibold">
                    Phase Start Date
                  </Box>
                </VStack>
                <FormControl
                  isInvalid={
                    errors.phases && !!errors.phases[index]?.phaseStartDate
                  }
                  flexBasis="50%"
                  mt="2"
                >
                  <Input
                    {...register(`phases.${index}.phaseStartDate`, {
                      required: 'Phase Start Date is required',
                    })}
                    type="date"
                  />
                  <FormErrorMessage>
                    {errors.phases &&
                      errors.phases[index]?.phaseStartDate &&
                      errors.phases[index]?.phaseStartDate?.message}
                  </FormErrorMessage>
                </FormControl>
              </HStack>

              <HStack gap="4" align="flex-start" w="full">
                <VStack color="custom_blue.1" align="start" flexBasis="25%">
                  <Box fontSize="sm" fontWeight="semibold">
                    Phase Estimated End Date
                  </Box>
                </VStack>
                <FormControl
                  isInvalid={
                    errors.phases &&
                    !!errors.phases[index]?.phaseEstimatedEndDate
                  }
                  flexBasis="50%"
                  mt="2"
                >
                  <Input
                    {...register(`phases.${index}.phaseEstimatedEndDate`, {
                      required: 'Phase Estimated End Date is required',
                    })}
                    type="date"
                  />
                  <FormErrorMessage>
                    {errors.phases &&
                      errors.phases[index]?.phaseEstimatedEndDate &&
                      errors.phases[index]?.phaseEstimatedEndDate?.message}
                  </FormErrorMessage>
                </FormControl>
              </HStack>
            </VStack>
          ))}
        </Box>

        {!phaseId && (
          <HStack justify="space-between">
            <Button onClick={() => handleAddPhase(false)}>
              <Text>Back</Text>
            </Button>
            <Button
              isLoading={isLoading}
              type="button"
              onClick={() => append(newPhase)}
              bg="custom_orange.2"
              _hover={{ bg: 'custom_orange.2' }}
              color="white"
            >
              Add Another Phase
            </Button>
          </HStack>
        )}
      </VStack>
    </Box>
  );
};
